import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import { defaultCurrentCyOption } from '@nxsys/constants/AppConstants';
import { useGetAgencyById, useUpdateAgencyById } from '@nxsys/hooks/api/agency';
import { useGoBackByQuery } from '@nxsys/hooks/useGoBackByQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { UpdateAgencyByIdParams } from '@nxsys/types/api';
import { AgencyContactType } from '@nxsys/types/models/permissions';
import { countryList } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { Space } from 'antd';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import AgencyForm, {
  AgencyFormRef,
  AgencyFormValues,
} from '../components/AgencyForm';

const EditGeneralInformation = () => {
  const { messages } = useTypedIntl();

  const formRef = useRef<AgencyFormRef>(null);
  const {
    run: updateAgency,
    loading: isUpdatingAgency,
    value: updateAgencyValue,
  } = useUpdateAgencyById();
  const updatedData = updateAgencyValue?.data;
  const params = useParams();
  const agencyId = params.agencyId;
  const {
    run: fetchAgency,
    loading: isFetchingAgency,
    value: agencyValue,
  } = useGetAgencyById();
  const agencyData = useMemo(() => agencyValue?.data, [agencyValue]);
  const data = updatedData ?? agencyData;

  useEffect(() => {
    if (agencyId) fetchAgency(agencyId);
  }, [agencyId]);

  const { goBack } = useGoBackByQuery();

  const handleGoBack = () =>
    goBack({
      fromPathQueryKey: 'from',
      fallbackPathDefault: agencyId ? `/agency/${agencyId}` : '/agency',
    });

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <div className="flex items-center gap-x-3">
          <AppPageTitle
            separator=""
            prefix={messages['common.editGeneralInformation']}
            title=""
          />
        </div>
      </div>
    );
  }, [messages]);

  const renderRightContent = useCallback(() => {
    return (
      <div className="flex items-center justify-between">
        <Space>
          <AppButton
            variant="outlined"
            type="primary"
            name={messages['common.cancel'] || 'Cancel'}
            onClick={handleGoBack}
          />
          <AppButton
            type="primary"
            name={messages['common.save'] || 'Save'}
            onClick={() => {
              formRef.current?.submit();
            }}
            loading={isUpdatingAgency}
            disabled={isUpdatingAgency}
          />
        </Space>
      </div>
    );
  }, [messages, formRef, isUpdatingAgency]);

  const handleSubmit = (values: AgencyFormValues) => {
    if (!agencyId) return;

    const params: UpdateAgencyByIdParams = {
      name: values.agencyName ?? '',
      agencyPrefix: values.agencyPrefix ?? '',
      currency: values.currency?.value ?? null,
      paymentModel: values.paymentModel?.map((item) => item.value) ?? [],
      allowPortalAccess: values.allowPortalAccess ?? null,
      enableBenefits: values.enableBenefits ?? null,
      convertRate: values.convertRate || '',
      email: values.contactDetails?.email || null,
      emergencyTelephone: values?.emergencyTelephone || null,
      mobile: values.contactDetails?.mobile || null,
      address: [
        values.contactDetails?.address1 || '',
        values.contactDetails?.address2 || '',
        values.contactDetails?.address3 || '',
      ],
      city: values.contactDetails?.city || '',
      county: values.contactDetails?.county || '',
      postCode: values.contactDetails?.postCode || '',
      country: values.contactDetails?.country?.value || '',
      onboardingEmailNotification:
        values.contactDetails?.onboardingEmailNotification || [],
      timesheetApprovalNeeded: values.timesheetApprovalNeeded || '',
      contactName: values.contactDetails?.name || '',
      emergencyContactName: values.emergencyContactName || '',
      emergencyMobile: values.emergencyMobile || null,
      emergencyEmail: values.emergencyEmail || null,
      timesheetApprover:
        values.timesheetApprover?.map((item: any) => item.value) || [],
    };

    updateAgency(agencyId, params).then(() => {
      showMessage({
        type: 'success',
        content: messages['prospect.agency.updateSuccess'],
      });
    });
  };

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={isFetchingAgency}
    >
      {data && (
        <AgencyForm
          formRef={formRef}
          onSubmit={handleSubmit}
          defaultValues={{
            agencyName: data?.name,
            timesheetApprovalNeeded: data?.timesheetApprovalNeeded,
            timesheetApprover: !data?.timesheetApprover
              ? []
              : data?.timesheetApprover.map((item: AgencyContactType) => ({
                  label: item.name,
                  value: item.id,
                })),
            allowPortalAccess: data?.allowPortalAccess,
            enableBenefits: data?.enableBenefits,
            paymentModel: data?.paymentModel
              ? data?.paymentModel.map((item: string) => ({
                  label: item,
                  value: item,
                }))
              : [],
            currency: data?.currency
              ? defaultCurrentCyOption.find(
                  (item) => item.value === data?.currency,
                )
              : null,
            convertRate: data?.convertRate,
            emergencyContactName: data?.emergencyContactName,
            emergencyEmail: data?.emergencyEmail,
            emergencyMobile: data?.emergencyMobile,
            emergencyTelephone: data?.emergencyTelephone,
            contactDetails: {
              name: data?.contactName,
              email: data?.email,
              mobile: data?.mobile,
              address1: data?.address?.[0] ?? '',
              address2: data?.address?.[1] ?? '',
              address3: data?.address?.[2] ?? '',
              city: data?.city,
              county: data?.county,
              postCode: data?.postCode,
              country: data?.country
                ? {
                    label:
                      countryList.find((item) => item.value === data?.country)
                        ?.label ?? '',
                    value: data?.country,
                  }
                : null,
              onboardingEmailNotification: data?.onboardingEmailNotification,
            },
          }}
        />
      )}
    </AppsContainer>
  );
};

export default EditGeneralInformation;
